import { api, ApiError } from './client'

export type CoachProgress = 'starting' | 'rebuilding' | 'retrying'
export const coachProgressLabel: Record<CoachProgress, string> = {
  starting: 'Preparing workspace…', rebuilding: 'Rebuilding workspace…', retrying: 'Retrying…',
}

/** Retry the identical send within one deadline; the backend owns workspace recovery. */
export async function postWhenCoachReady<T>(
  path: string,
  body: unknown,
  options: { onProgress?: (progress: CoachProgress) => void; signal?: AbortSignal } = {},
): Promise<T> {
  const deadline = Date.now() + 180_000
  let delay = 3000
  let transientRetries = 0
  while (true) {
    options.signal?.throwIfAborted()
    if (Date.now() >= deadline) throw new Error('The coach is taking longer than usual. Retry to finish sending your message.')
    try {
      return await api.post<T>(path, body, { signal: options.signal })
    } catch (error) {
      if (!(error instanceof ApiError)) throw error
      const details = error.payload as { code?: string; retry_after_seconds?: number }
      const progress: CoachProgress | undefined = details?.code === 'workspace_starting' ? 'starting'
        : details?.code === 'workspace_recovering' ? 'rebuilding'
        : details?.code === 'coach_temporary_error' ? 'retrying' : undefined
      if (!progress || (progress === 'retrying' && transientRetries++ >= 3)) throw error
      if (Date.now() >= deadline) {
        throw new Error('The coach is taking longer than usual. Retry to finish sending your message.')
      }
      options.onProgress?.(progress)
      const requestedDelay = details.retry_after_seconds
      const wait = typeof requestedDelay === 'number' && Number.isFinite(requestedDelay)
        ? Math.max(delay, Math.min(180_000, requestedDelay * 1000)) : delay
      await new Promise<void>((resolve, reject) => {
        const stop = () => {
          window.clearTimeout(timer)
          options.signal?.removeEventListener('abort', stop)
          reject(options.signal?.reason)
        }
        const timer = window.setTimeout(
          () => {
            options.signal?.removeEventListener('abort', stop)
            resolve()
          },
          Math.min(wait, deadline - Date.now()),
        )
        options.signal?.addEventListener('abort', stop, { once: true })
        if (options.signal?.aborted) stop()
      })
      delay = Math.min(delay * 1.5, 10_000)
    }
  }
}
